import { qz } from "../client";
import { createNamespaceStore } from "../internal/createNamespaceStore";
import {
  type HidEvent,
  type HidKeyInput,
  type HidOpenStreamInput,
  type HidState,
  type HidStatus,
} from "./types";

const MAX_HID_EVENTS = 100;

const hidKey = (input: Partial<HidKeyInput>) =>
  [
    input.vendorId ?? "",
    input.productId ?? "",
    input.usagePage ?? "",
    input.serial ?? "",
  ].join(":");

const toHidStatus = (event: HidEvent, updatedAt: number): HidStatus => ({
  lastActionType: event.kind === "ACTION" ? event.actionType : null,
  lastEventKind: event.kind,
  lastException: event.kind === "ERROR" ? event.exception : null,
  lastOutput: event.kind === "RECEIVE" ? event.output : null,
  productId: event.productId,
  updatedAt,
  vendorId: event.vendorId,
});

const parseHidEvent = (raw: unknown): HidEvent | null => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const data = raw as Record<string, unknown>;
  const vendorId = typeof data.vendorId === "string" ? data.vendorId : null;
  const productId = typeof data.productId === "string" ? data.productId : null;

  if (!vendorId || !productId) return null;

  if (typeof data.actionType === "string") {
    return {
      actionType: data.actionType,
      kind: "ACTION",
      productId,
      raw,
      vendorId,
    };
  }

  if (typeof data.exception === "string") {
    return {
      exception: data.exception,
      kind: "ERROR",
      productId,
      raw,
      vendorId,
    };
  }

  if ("output" in data) {
    return {
      kind: "RECEIVE",
      output: data.output,
      productId,
      raw,
      vendorId,
    };
  }

  return { kind: "STATUS", productId, raw, vendorId };
};

export const hidStore = createNamespaceStore<HidState, HidEvent>({
  applyEvent: (state, event, at) => ({
    ...state,
    events: [...state.events, event].slice(-MAX_HID_EVENTS),
    lastEventAt: at,
    statusByDevice: {
      ...state.statusByDevice,
      [hidKey(event)]: toHidStatus(event, at),
    },
  }),
  createInitialState: () => ({
    activeStreams: {},
    claimedDevices: {},
    events: [],
    isListening: false,
    lastEventAt: null,
    lastListenerError: null,
    statusByDevice: {},
  }),
  label: "hid",
  parseEvent: parseHidEvent,
  registerCallback: (handler) => qz.hid.setHidCallbacks(handler),
});

export const markHidClaimed = (input: HidKeyInput, claimed: boolean) => {
  hidStore.setState((s) => ({
    ...s,
    claimedDevices: {
      ...s.claimedDevices,
      [hidKey(input)]: claimed,
    },
  }));
};

export const markHidStreamOpened = (input: HidOpenStreamInput) => {
  hidStore.setState((s) => ({
    ...s,
    activeStreams: {
      ...s.activeStreams,
      [hidKey(input)]: input,
    },
  }));
};

export const markHidStreamClosed = (input: HidKeyInput) => {
  const key = hidKey(input);
  hidStore.setState((s) => {
    const activeStreams = { ...s.activeStreams };
    delete activeStreams[key];
    return { ...s, activeStreams };
  });
};

export const startHidListeningState = () => {
  hidStore.setState((s) => ({ ...s, isListening: true }));
};

export const stopHidListeningState = () => {
  hidStore.setState((s) => ({ ...s, isListening: false }));
};
